import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { useMemo } from 'react';

import Directory from '@/components/features/files/Directory';
import File from '@/components/features/files/File';
import DirectoryCardSkeleton from '@/components/features/files/FsDirectoryCardSkeleton';
import FileCardSkeleton from '@/components/features/files/FsFileCardSkeleton';
import { Label } from '@/components/ui/label/Label';

interface FilesContentGrid {
  files?: FilePublicWithPermissions[];
  directories?: DirectoryPublic[];
  isSuccess: boolean;
  isLoading: boolean;
  handleNavigateTo: (directoryId: string) => void;
}

export default function FilesContentGrid(filesContentGrid: FilesContentGrid) {
  const { files = [], directories = [], isLoading, isSuccess, handleNavigateTo } = filesContentGrid;

  const hasDirectories: boolean = useMemo(() => isSuccess && directories.length > 0, [isSuccess, directories]);
  const hasFiles: boolean = useMemo(() => isSuccess && files.length > 0, [isSuccess, files]);

  return (
    <div className="flex h-full grow flex-col gap-4">
      {/* Directories Section */}
      {(isLoading || hasDirectories) && (
        <div className="flex flex-col">
          <Label className="text-muted-foreground text-sm">Foldery</Label>
          <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(256px,1fr))] grid-rows-[auto] gap-4">
            {isLoading ? (
              <>
                <DirectoryCardSkeleton />
                <DirectoryCardSkeleton />
                <DirectoryCardSkeleton />
              </>
            ) : (
              directories.map((directory) => <Directory key={directory.id} directory={directory} handleNavigateTo={handleNavigateTo} />)
            )}
          </div>
        </div>
      )}

      {/* Files Section */}
      {(isLoading || hasFiles) && (
        <div className="flex flex-col">
          <Label className="text-muted-foreground text-sm">Pliki</Label>
          <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(256px,1fr))] grid-rows-[auto] gap-4">
            {isLoading ? (
              <>
                <FileCardSkeleton />
                <FileCardSkeleton />
                <FileCardSkeleton />
                <FileCardSkeleton />
              </>
            ) : (
              files.map((file) => <File key={file.id} file={file} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
